import { t } from "i18next";
import React, { FC, useRef, useState } from "react";
import { Image } from "../types/models/Image";

interface FileInputProps {
  image?: Image;
  image_url?: string;
  setImage: (image?: Image) => void;
  onAddImage?: (file: File) => void;
  onRemoveImage?: (id: number) => void;
  accept?: string;
  label?: string;
}

export const ImageUploader: FC<FileInputProps> = ({
  image,
  image_url,
  setImage,
  onAddImage,
  onRemoveImage,
  accept = "image/jpeg, image/png, image/jpg",
  label = t("addPicturesModal.upload"),
}) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const newFile = files[0];
      handleAddImage(newFile);
    }
  };

  const handleAddImage = (file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const newImage = { file: file, url: reader.result as string };
      setImage(newImage);
      onAddImage?.(file);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  const removeImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (image === undefined) return;

    const deletedImage = image;
    setImage(undefined);
    if (deletedImage.id !== undefined) {
      onRemoveImage?.(deletedImage.id);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleAddImage(files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="flex flex-col space-y-4 px-4">
      <div className="flex flex-1 flex-row items-start space-x-4 overflow-y-scroll">
        <div
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`flex flex-col flex-1 items-center justify-center w-40 h-60 bg-delicate-50 rounded-lg cursor-pointer border-2 relative ${
            isDragging
              ? "border-rust-300 border-dashed"
              : "border-slate-300 border-dashed"
          } transition-colors duration-200 ease-in-out flex-shrink-0`}
        >
          {image ? (
            <div className="relative w-full h-full">
              <img
                src={image.url || image_url}
                alt="Dernière image"
                className="w-full h-full object-cover rounded-lg"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeImage();
                }}
                className="absolute top-2 right-2 bg-white bg-opacity-70 rounded-full p-1 hover:bg-opacity-100 transition-opacity"
                title="Supprimer l'image"
              >
                <svg
                  className="w-6 h-6 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <div className="h-16 w-16 mb-3 rounded-full border-2 border-rust-300 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-rust-300"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="text-rust-300 text-sm w-2/3 font-medium text-center">
                {label}
              </div>
              <p className="text-sm text-slate-500 mt-2">0/1 images</p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={accept}
            onChange={handleFileChange}
          />
        </div>
      </div>
    </div>
  );
};
